import { ConfirmationModal, ConfirmationModalProps } from '@cabonline/ui-web';
import { InfoOutlined } from '@mui/icons-material';
import { useLocale } from '@multiplatform-apps/service-trips';
import { useDeviceType } from '../hooks';
import React, { PropsWithChildren, createContext, useCallback, useMemo, useState } from 'react';

import { useModal } from './modal-context';

type ConfirmationModalData = Required<Pick<ConfirmationModalProps, 'title'>> &
  Partial<Pick<ConfirmationModalProps, 'onConfirm' | 'icon' | 'text'>>;

interface ConfirmationContextType {
  show: (data: ConfirmationModalData) => void;
  hide: () => void;
}

const ConfirmationContext = createContext<ConfirmationContextType>(
  {} as ConfirmationContextType
);

export const ConfirmationProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [data, setData] = useState<ConfirmationModalData | null>();

  const { isAnyModalOpen } = useModal();
  const { t } = useLocale();
  const deviceType = useDeviceType();

  const show = useCallback((confirmationData: ConfirmationModalData) => {
    setData(confirmationData);
  }, []);

  const hide = useCallback(() => {
    setData(undefined);
  }, []);

  const value = useMemo(() => ({ show, hide }), [show, hide]);

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      {data && (
        <ConfirmationModal
          icon={data.icon || <InfoOutlined fontSize="large" />}
          title={data.title}
          text={data.text}
          onConfirm={data.onConfirm}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('common.cancelButton')}
          onCancel={hide}
          isSecondaryModal={isAnyModalOpen}
          onClose={hide}
          isDesktop={deviceType === 'desktop'}
        />
      )}
    </ConfirmationContext.Provider>
  );
};

export const useConfirmationModal = () => React.useContext(ConfirmationContext);
